/**
 * Profile Page
 * User profile view and edit for all roles
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../components/Layout/MainLayout';
import { 
  MdPerson, 
  MdEmail, 
  MdPhone, 
  MdEdit,
  MdSave,
  MdCancel,
  MdVerifiedUser
} from 'react-icons/md';
const ProfilePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    dateOfBirth: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phoneNumber: user.phoneNumber || '',
        dateOfBirth: user.dateOfBirth ? user.dateOfBirth.split('T')[0] : ''
      });
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEdit = () => {
    setIsEditing(true);
    setMessage({ type: '', text: '' });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData({
      name: user.name || '',
      email: user.email || '',
      phoneNumber: user.phoneNumber || '',
      dateOfBirth: user.dateOfBirth ? user.dateOfBirth.split('T')[0] : ''
    });
    setMessage({ type: '', text: '' });
  };

  const handleSave = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // TODO: Implement API call to update profile
      // const response = await userService.updateProfile(formData);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
      setIsEditing(false);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update profile. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadgeClass = (role) => {
    const baseClasses = 'inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-semibold uppercase tracking-wide';
    switch (role) {
      case 'ADMIN':
        return `${baseClasses} bg-accent-terracotta/15 text-accent-terracotta-dark`;
      case 'HR':
        return `${baseClasses} bg-accent-green/15 text-accent-green-dark`;
      case 'HRO':
        return `${baseClasses} bg-accent-terracotta/15 text-accent-terracotta-dark`;
      case 'CHAIRMAN':
        return `${baseClasses} bg-status-warning-bg text-[#B8842E]`;
      case 'CANDIDATE':
        return `${baseClasses} bg-status-processing-bg text-status-processing`;
      default:
        return `${baseClasses} bg-background-light text-text-secondary`;
    }
  };

  return (
    <MainLayout>
      <div className="max-w-[1200px] mx-auto p-6">
        <div className="flex justify-between items-center mb-6 p-6 bg-surface rounded-lg shadow-sm border border-border-light">
          <div>
            <h1 className="text-[28px] text-primary m-0 mb-1 font-semibold">My Profile</h1>
            <p className="text-sm text-text-secondary m-0">Manage your account information</p>
          </div>
          {!isEditing && (
            <button 
              className="flex items-center gap-2 px-5 py-2.5 bg-accent-green text-white border-none rounded-md font-semibold text-sm cursor-pointer transition-all duration-200 hover:bg-[#5a6b4d] hover:-translate-y-px"
              onClick={handleEdit}
            >
              <MdEdit size={18} />
              Edit Profile
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6">
          <div className="bg-surface rounded-lg p-8 shadow-sm border border-border-light">
            <div className="flex flex-col items-center mb-8 pb-6 border-b-2 border-border-light">
              <div className="w-[120px] h-[120px] rounded-full bg-gradient-to-br from-accent-green to-accent-terracotta flex items-center justify-center text-white mb-4 shadow-[0_4px_12px_rgba(107,127,92,0.3)]">
                <MdPerson size={64} />
              </div>
              <div className="mt-2">
                <span className={getRoleBadgeClass(user?.role)}>
                  <MdVerifiedUser size={16} />
                  {user?.role}
                </span>
              </div>
            </div>

            {message.text && (
              <div className={`px-4 py-3 rounded-md mb-6 text-sm font-medium ${
                message.type === 'success' 
                  ? 'bg-status-success-bg text-status-success border border-status-success' 
                  : 'bg-status-error-bg text-status-error border border-status-error'
              }`}>
                {message.text}
              </div>
            )}

            <div className="flex flex-col gap-5">
              <div className="flex flex-col gap-2">
                <label htmlFor="name" className="flex items-center gap-2 text-sm font-semibold text-primary">
                  <MdPerson size={20} />
                  Full Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  disabled={!isEditing || loading}
                  placeholder="Enter your full name"
                  className="px-4 py-3 border-2 border-border-light rounded-md text-sm text-text-primary bg-background-light transition-all duration-200 focus:outline-none focus:border-accent-green focus:bg-white disabled:bg-background disabled:text-text-secondary disabled:cursor-not-allowed"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="email" className="flex items-center gap-2 text-sm font-semibold text-primary">
                  <MdEmail size={20} />
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={!isEditing || loading}
                  placeholder="Enter your email"
                  className="px-4 py-3 border-2 border-border-light rounded-md text-sm text-text-primary bg-background-light transition-all duration-200 focus:outline-none focus:border-accent-green focus:bg-white disabled:bg-background disabled:text-text-secondary disabled:cursor-not-allowed"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="phoneNumber" className="flex items-center gap-2 text-sm font-semibold text-primary">
                  <MdPhone size={20} />
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  disabled={true}
                  placeholder="Phone number (cannot be changed)"
                  className="px-4 py-3 border-2 border-border-light rounded-md text-sm text-text-primary bg-background-light transition-all duration-200 focus:outline-none focus:border-accent-green focus:bg-white disabled:bg-background disabled:text-text-secondary disabled:cursor-not-allowed"
                />
                <small className="text-xs text-text-secondary italic">Phone number cannot be changed as it's used for login</small>
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="dateOfBirth" className="flex items-center gap-2 text-sm font-semibold text-primary">
                  <MdPerson size={20} />
                  Date of Birth
                </label>
                <input
                  type="date"
                  id="dateOfBirth"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleInputChange}
                  disabled={!isEditing || loading}
                  max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                  min={new Date(new Date().setFullYear(new Date().getFullYear() - 65)).toISOString().split('T')[0]}
                  className="px-4 py-3 border-2 border-border-light rounded-md text-sm text-text-primary bg-background-light transition-all duration-200 focus:outline-none focus:border-accent-green focus:bg-white disabled:bg-background disabled:text-text-secondary disabled:cursor-not-allowed"
                />
              </div>

              {isEditing && (
                <div className="flex gap-3 justify-end mt-2 pt-6 border-t-2 border-border-light">
                  <button 
                    className="flex items-center gap-2 px-5 py-2.5 border-2 border-border bg-background text-text-primary rounded-md font-semibold text-sm cursor-pointer transition-all duration-200 hover:bg-background-light hover:border-text-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    <MdCancel size={18} />
                    Cancel
                  </button>
                  <button 
                    className="flex items-center gap-2 px-5 py-2.5 bg-accent-green text-white border-none rounded-md font-semibold text-sm cursor-pointer transition-all duration-200 hover:bg-[#5a6b4d] hover:-translate-y-px disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleSave}
                    disabled={loading}
                  >
                    <MdSave size={18} />
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="bg-surface rounded-lg p-8 shadow-sm border border-border-light">
            <h3 className="text-lg text-primary m-0 mb-5 pb-3 border-b-2 border-accent-green font-semibold">Account Information</h3>
            <div className="flex flex-col gap-4">
              <div className="flex justify-between items-center py-3 border-b border-border-light last:border-b-0">
                <span className="text-[13px] font-semibold text-text-secondary uppercase tracking-wide">Account Status</span>
                <span className={`px-3 py-1.5 rounded-full text-xs font-semibold tracking-wide ${
                  user?.isActive 
                    ? 'bg-status-success-bg text-status-success' 
                    : 'bg-status-error-bg text-status-error'
                }`}>
                  {user?.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-border-light last:border-b-0">
                <span className="text-[13px] font-semibold text-text-secondary uppercase tracking-wide">Member Since</span>
                <span className="text-sm text-text-primary font-medium">
                  {user?.createdAt ? new Date(user.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  }) : 'N/A'}
                </span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-border-light last:border-b-0">
                <span className="text-[13px] font-semibold text-text-secondary uppercase tracking-wide">User ID</span>
                <span className="text-sm text-text-primary font-medium">{user?.id || 'N/A'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default ProfilePage;
